/**
 * Permission Message Mapping Configuration
 *
 * Centralizes all permission-denied messages for consistent UX across the application.
 * Maps API endpoints and error responses to user-friendly messages.
 *
 * HIPAA Compliance Note:
 * - Messages are intentionally vague to avoid leaking information about system structure
 * - No specific patient IDs, user details, or resource identifiers are included
 * - All permission denials are logged for audit purposes
 *
 * @see {@link file://./../hooks/usePermissionHandler.tsx} Permission Handler Hook
 */

export interface PermissionErrorDetail {
  url?: string;
  method?: string;
  status?: number;
  suppressGlobalNotice?: boolean;
  response?: {
    code?: string;
    error?: string;
    message?: string;
    resource?: string;
    required_permission?: string;
    error_code?: string;
    feature?: string;
    organization_id?: string;
    organizationId?: string;
  };
}

/**
 * Get user-friendly permission message based on error details
 * Falls back to generic message if no specific match found
 */
export function getPermissionMessage(
  detail: PermissionErrorDetail
): { title: string; message: string; icon: string } {
  const { url = '', method = 'GET', response } = detail;

  // Use backend message if available and informative
  if (response?.message && response.message.length > 20) {
    return {
      title: 'Access Denied',
      message: response.message,
      icon: '🔒',
    };
  }

  // Patient Records
  if (url.includes('/patients/')) {
    if (method === 'GET') {
      return {
        title: 'Patient Record Access Denied',
        message:
          'You do not have permission to view this patient record. Please contact your department administrator if you believe you should have access.',
        icon: '🏥',
      };
    } else if (['POST', 'PUT', 'PATCH'].includes(method)) {
      return {
        title: 'Patient Update Denied',
        message:
          'You do not have permission to modify patient records. This action requires elevated privileges.',
        icon: '✏️',
      };
    } else if (method === 'DELETE') {
      return {
        title: 'Patient Deletion Denied',
        message:
          'You do not have permission to delete patient records. This action is restricted to administrators.',
        icon: '🗑️',
      };
    }
  }

  // EEG Studies and Recordings
  if (url.includes('/eeg-records/') || url.includes('/studies/')) {
    if (method === 'GET') {
      return {
        title: 'EEG Study Access Denied',
        message:
          'You do not have permission to view this EEG study. Access may be restricted based on department or patient assignment.',
        icon: '📊',
      };
    } else if (['POST', 'PUT', 'PATCH'].includes(method)) {
      return {
        title: 'EEG Study Modification Denied',
        message:
          'You do not have permission to modify EEG studies. Please verify your role permissions.',
        icon: '⚡',
      };
    }
  }

  // Reports
  if (url.includes('/reports/')) {
    if (method === 'GET') {
      return {
        title: 'Report Access Denied',
        message:
          'You do not have permission to view this report. Reports may be restricted to the authoring physician or assigned reviewers.',
        icon: '📄',
      };
    } else if (method === 'POST') {
      return {
        title: 'Report Creation Denied',
        message:
          'You do not have permission to create reports. This action is typically restricted to physicians and specialists.',
        icon: '📝',
      };
    } else if (['PUT', 'PATCH'].includes(method)) {
      return {
        title: 'Report Edit Denied',
        message:
          'You do not have permission to edit this report. Only the report author or assigned reviewers can make changes.',
        icon: '✏️',
      };
    } else if (method === 'DELETE') {
      return {
        title: 'Report Deletion Denied',
        message:
          'You do not have permission to delete reports. This action requires administrator privileges.',
        icon: '🗑️',
      };
    }
  }

  // User Management
  if (url.includes('/users/')) {
    if (method === 'GET' && url.includes('/users/me')) {
      return {
        title: 'Profile Access Denied',
        message:
          'Unable to access your user profile. Your session may have expired. Please log in again.',
        icon: '👤',
      };
    } else if (method === 'GET') {
      return {
        title: 'User Directory Access Denied',
        message:
          'You do not have permission to view user information. This feature is restricted to administrators.',
        icon: '👥',
      };
    } else if (['POST', 'PUT', 'PATCH'].includes(method)) {
      return {
        title: 'User Management Denied',
        message:
          'You do not have permission to manage user accounts. Contact your system administrator.',
        icon: '⚙️',
      };
    }
  }

  // Appointments
  if (url.includes('/appointments/')) {
    return {
      title: 'Appointment Access Denied',
      message:
        'You do not have permission to manage appointments. This feature may be restricted based on your role or department.',
      icon: '📅',
    };
  }

  // Settings and Configuration
  if (url.includes('/settings/') || url.includes('/config/')) {
    return {
      title: 'Settings Access Denied',
      message:
        'You do not have permission to modify system settings. This action is restricted to administrators.',
      icon: '⚙️',
    };
  }

  // Montages and Perspectives
  if (url.includes('/montages/') || url.includes('/perspectives/')) {
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      return {
        title: 'Configuration Modification Denied',
        message:
          'You do not have permission to modify system configurations. Custom configurations must be created under your own user account.',
        icon: '🔧',
      };
    }
  }

  // Audit Logs and Security
  if (url.includes('/audit/') || url.includes('/security/')) {
    return {
      title: 'Security Data Access Denied',
      message:
        'You do not have permission to view security logs. This feature is restricted to security administrators.',
      icon: '🔐',
    };
  }

  // Billing invoices and payments
  if (
    url.includes('/billing/') &&
    (url.includes('/payments') ||
      url.includes('/invoices') ||
      url.includes('/stripe/') ||
      url.includes('/org-commercial'))
  ) {
    return {
      title: 'Payment Access Denied',
      message:
        'You do not have permission to view invoices or payment details. Contact your organization administrator if you need access.',
      icon: '🔒',
    };
  }

  // Help System
  if (url.includes('/help/')) {
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      return {
        title: 'Help Content Modification Denied',
        message:
          'You do not have permission to modify help articles. Content management is restricted to administrators.',
        icon: '❓',
      };
    }
  }

  // Storage Paths (Admin only)
  if (url.includes('/storage-paths/')) {
    return {
      title: 'Storage Management Denied',
      message:
        'You do not have permission to manage storage paths. This is a super-administrator feature.',
      icon: '💾',
    };
  }

  // Default fallback message
  return {
    title: 'Access Denied',
    message:
      'You do not have the required permissions to perform this action. If you believe this is an error, please contact your system administrator.',
    icon: '🔒',
  };
}

/**
 * Get a short one-line message for inline display (e.g., in cards)
 */
export function getInlinePermissionMessage(url?: string): string {
  if (!url) return 'Access restricted';

  if (url.includes('/patients/')) return 'Patient record access restricted';
  if (url.includes('/eeg-records/') || url.includes('/studies/'))
    return 'EEG study access restricted';
  if (url.includes('/reports/')) return 'Report access restricted';
  if (url.includes('/users/')) return 'User information restricted';
  if (url.includes('/appointments/')) return 'Appointment access restricted';
  if (url.includes('/billing/')) return 'Payment access restricted';

  return 'Access restricted';
}

/**
 * Determine if permission error should show inline fallback vs toast
 * Some critical operations should block the UI, others can show a toast
 */
export function shouldShowInlineFallback(
  url?: string,
  method?: string
): boolean {
  // Always show inline fallback for GET requests (data display)
  if (method === 'GET') return true;

  // Show inline for critical resources
  if (url?.includes('/patients/') || url?.includes('/studies/')) return true;

  // Everything else can use toast notifications
  return false;
}
